/**
 * Resolves a cross-component signal (approvalRequest / rejectRequest /
 * commercialPopoverRequest — see CreatorsTab.tsx's three consuming effects)
 * against the current page's rows, distinguishing "still loading, wait for
 * the next render" from "settled and genuinely not here". The caller must
 * clear the signal and tell the user on the latter — never leave a one-shot
 * signal armed indefinitely, which would pop a dialog open unexpectedly
 * whenever the user later happens to navigate to wherever the row lives.
 *
 * `rows` is deliberately the CURRENT PAGE's rows, not the whole pill bucket
 * — `activeRows`/`visibleCreators` in CreatorsTab.tsx are both page-scoped
 * (confirmed: useRosterStatusTabs.ts's `entries` is `serverPage.entries`,
 * one page). A row that's genuinely on a different page than the one the
 * resumed navigation landed on is indistinguishable, from here, from a row
 * that plain doesn't exist — both resolve to 'absent'. This is a known,
 * accepted limitation (not solved by this helper): the caller gives up and
 * tells the user rather than silently hanging forever OR silently dropping
 * the action with no explanation, which are the two failure modes this
 * helper exists to prevent.
 */
export type SignalRowOutcome<T> =
  | { kind: 'wait' }
  | { kind: 'absent' }
  | { kind: 'found'; row: T };

export function resolveSignalRow<T extends { id: string | number }>(
  rows: readonly T[],
  targetId: string,
  isSettled: boolean,
): SignalRowOutcome<T> {
  const row = rows.find((r) => String(r.id) === targetId);
  if (row) return { kind: 'found', row };
  if (!isSettled) return { kind: 'wait' };
  return { kind: 'absent' };
}
